import { useEffect, useId, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Loader2, Lock, UserPlus, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import {
  useCreateClient360Contact,
  useUpdateClient360Contact,
} from "@/hooks/useClients";
import {
  contactDuplicateDryRun,
  type Client360ContactCard,
  type Client360ContactsResponse,
  type ContactClientType,
  type ContactDuplicateCandidate,
} from "@/api/clients";

/**
 * Этап 2 «Контакти» — add / edit drawer. Built to
 * VISUAL_Client360_Contacts_v1_2_LOCKED.html in the shared shell with DS tokens.
 *
 *  • HARD-required: ПІБ only. Roles / methods are SOFT → saved as incomplete.
 *  • Source is SYSTEM-SET (manual_manager) — read-only badge, never editable.
 *  • Method visibility is backend/ACL-owned → read-only «🔒 за правилами доступу».
 *  • Duplicate (AC-7): synchronous dry-run warns inline; manager may acknowledge
 *    and save, or open the candidate — merge stays supervisor-only.
 *  • §8 type-aware (OPTION A = hide): non-B2B clients (breeder / phys / service
 *    types) get the LEAN form — ПІБ + методи + «основний для клієнта»; the B2B
 *    block (roles chip-set, привʼязка, основний-для-замовлень/оплат) is hidden.
 *    Unclassified → lean default + «Розгорнути додаткові поля» expander.
 *    Lean save stays VALID: roles auto-default to ["general"] («інше») so the
 *    assignment exists (primary works, keyContacts fill, no missing_role DQ).
 */

type ContactFormMode = "b2b" | "lean" | "unclassified";

/** record_type values that are NOT a quasi-юрособа (§8). */
const NON_B2B_RECORD_TYPES = new Set([
  "breeder_person",
  "retail_b2c_customer",
  "technical_account",
  "event_exhibition_account",
  "internal_account",
]);

/** §8 signal precedence: non-B2B record_type WINS over partyType (rule 3). */
export function contactFormMode(ct?: ContactClientType | null): ContactFormMode {
  if (!ct) return "unclassified";
  if (NON_B2B_RECORD_TYPES.has(ct.recordType) || ct.businessModel === "breeder_professional") {
    return "lean";
  }
  if (ct.recordType === "real_b2b_client" || ct.partyType === "organization") return "b2b";
  if (ct.partyType === "physical_person") return "lean";
  return "unclassified";
}

const ROLE_OPTIONS: { code: string; label: string }[] = [
  { code: "decision_maker", label: "ЛПР" },
  { code: "purchasing", label: "закупівельник" },
  { code: "accountant", label: "бухгалтер" },
  { code: "store_manager", label: "керівник точки" },
  { code: "contract_signatory", label: "підписант" },
  { code: "it", label: "технічний" },
  { code: "general", label: "інше" },
];

const METHOD_TYPES: { ui: string; backend: string; label: string }[] = [
  { ui: "phone", backend: "phone_mobile", label: "📞 телефон" },
  { ui: "email", backend: "email_work", label: "✉ email" },
  { ui: "viber", backend: "viber", label: "🟣 Viber" },
  { ui: "telegram", backend: "telegram_username", label: "Telegram" },
  { ui: "whatsapp", backend: "whatsapp", label: "WhatsApp" },
];

interface MethodRow {
  backendType: string;
  value: string;
}

interface Props {
  open: boolean;
  holdingId: string;
  clientName: string;
  scopes: Client360ContactsResponse["scopes"];
  /** present → edit mode */
  editContact?: Client360ContactCard | null;
  /** Этап 4/5 — open with a scope preselected (LE «+ Прив'язати контакт» / outlet «+ новий контакт»). */
  prefillScope?:
    | { scope: "legal_entity"; targetAccountId: string }
    | { scope: "outlet"; targetOutletId: string }
    | null;
  /** CTA «основний контакт / власника» → primary pre-set (create mode only). */
  prefillPrimary?: boolean;
  /** §8 type-aware signals (from Client360ContactsResponse). Absent → lean+expander. */
  clientType?: ContactClientType | null;
  onClose: () => void;
}

export function ContactDrawer({ open, holdingId, clientName, scopes, editContact, prefillScope, prefillPrimary, clientType, onClose }: Props) {
  const headingId = useId();
  const panelRef = useDrawerA11y(open, onClose);
  const isEdit = !!editContact;
  const createMut = useCreateClient360Contact(holdingId);
  const updateMut = useUpdateClient360Contact(holdingId);

  const [pib, setPib] = useState("");
  const [position, setPosition] = useState("");
  const [roles, setRoles] = useState<string[]>([]);
  const [methods, setMethods] = useState<MethodRow[]>([{ backendType: "phone_mobile", value: "" }]);
  const [scope, setScope] = useState<"holding" | "legal_entity" | "outlet">("holding");
  const [targetAccountId, setTargetAccountId] = useState("");
  const [targetOutletId, setTargetOutletId] = useState("");
  const [primaryScope, setPrimaryScope] = useState(false);
  const [primaryOrders, setPrimaryOrders] = useState(false);
  const [primaryPayments, setPrimaryPayments] = useState(false);
  const [dupCandidates, setDupCandidates] = useState<ContactDuplicateCandidate[]>([]);
  const [acknowledged, setAcknowledged] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // §8 unclassified fallback — «Розгорнути додаткові поля» reveals the B2B block.
  const [b2bExpanded, setB2bExpanded] = useState(false);
  const dupTimer = useRef<number | null>(null);

  // §8 type-aware form mode (record_type wins; absent signals → lean+expander).
  const mode = contactFormMode(clientType);
  const showB2b = mode === "b2b" || (mode === "unclassified" && b2bExpanded);

  // (re)initialise on open / contact change
  useEffect(() => {
    if (!open) return;
    if (editContact) {
      setPib(editContact.name);
      setPosition(editContact.position ?? "");
      setRoles(editContact.roleCodes ?? []);
      setMethods([{ backendType: "phone_mobile", value: "" }]); // edit = APPEND new methods
    } else {
      setPib("");
      setPosition("");
      // CTA «основний/власника»: assignment (і тому primary) існує лише з роллю —
      // у B2B преселектимо «ЛПР» (можна змінити); у lean роль додається мовчки
      // як «інше» при збереженні (див. submit), тож тут лишаємо порожньо.
      setRoles(prefillPrimary && mode === "b2b" ? ["decision_maker"] : []);
      setMethods([{ backendType: "phone_mobile", value: "" }]);
    }
    setB2bExpanded(false);
    // Этап 4/5 — preselect the scope when opened from an LE card / outlet drawer.
    setScope("holding");
    setTargetAccountId("");
    setTargetOutletId("");
    if (!editContact && prefillScope?.scope === "legal_entity") {
      setScope("legal_entity");
      setTargetAccountId(prefillScope.targetAccountId);
    } else if (!editContact && prefillScope?.scope === "outlet") {
      setScope("outlet");
      setTargetOutletId(prefillScope.targetOutletId);
    }
    // CTA «основний/власника» → primary pre-set (create only; edit keeps reality).
    setPrimaryScope(!editContact && !!prefillPrimary);
    setPrimaryOrders(false);
    setPrimaryPayments(false);
    setDupCandidates([]);
    setAcknowledged(false);
    setErrorMsg(null);
  }, [open, editContact, prefillScope, prefillPrimary, mode]);

  // ПІБ → firstName/lastName parse (Ukrainian order: Прізвище Імʼя [По батькові])
  const parsedName = useMemo(() => {
    const parts = pib.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return { firstName: "", lastName: "", middle: "" };
    if (parts.length === 1) return { firstName: parts[0], lastName: parts[0], middle: "" };
    return { lastName: parts[0], firstName: parts[1], middle: parts.slice(2).join(" ") };
  }, [pib]);

  // synchronous duplicate pre-check (AC-7)
  useEffect(() => {
    if (!open || isEdit) return;
    const { firstName, lastName } = parsedName;
    if (firstName === "" || lastName === "") {
      setDupCandidates([]);
      return;
    }
    if (dupTimer.current) window.clearTimeout(dupTimer.current);
    dupTimer.current = window.setTimeout(() => {
      const mlist = methods.filter((m) => m.value.trim() !== "").map((m) => ({ value: m.value.trim() }));
      contactDuplicateDryRun(firstName, lastName, mlist)
        .then((r) => {
          setDupCandidates(r.candidates ?? []);
          setAcknowledged(false);
        })
        .catch(() => setDupCandidates([]));
    }, 450);
    return () => {
      if (dupTimer.current) window.clearTimeout(dupTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pib, JSON.stringify(methods), open, isEdit]);

  if (!open) return null;

  const toggleRole = (code: string) =>
    setRoles((r) => (r.includes(code) ? r.filter((x) => x !== code) : [...r, code]));

  const hasMethod = methods.some((m) => m.value.trim() !== "");
  // §8 lean: roles are hidden (auto «інше» on save) → don't warn about them.
  const willBeIncomplete = !hasMethod || (showB2b && roles.length === 0 && position.trim() === "");
  const canSubmit = parsedName.firstName !== "" && parsedName.lastName !== "" && !createMut.isPending && !updateMut.isPending;
  const scopeWord = scope === "holding" ? "клієнта" : scope === "legal_entity" ? "цієї юрособи" : "цієї точки";

  const submit = () => {
    if (!canSubmit) return;
    setErrorMsg(null);
    const methodPayload = methods
      .filter((m) => m.value.trim() !== "")
      .map((m) => ({ type: m.backendType, value: m.value.trim() }));
    // §8 lean: roles chip-set is hidden → silently default to «інше» (general) so
    // the assignment exists (primary works, keyContacts fill, no missing_role DQ).
    const effectiveRoles = showB2b ? roles : roles.length > 0 ? roles : ["general"];

    if (isEdit && editContact) {
      updateMut.mutate(
        {
          contactId: editContact.id,
          payload: {
            contact: {
              firstName: parsedName.firstName,
              lastName: parsedName.lastName,
              cCMiddleName: parsedName.middle || undefined,
              cCPosition: position.trim() || undefined,
            },
            addMethods: methodPayload,
            // lean edit: chips hidden → do NOT touch existing assignment roles.
            roles: showB2b ? roles : undefined,
          },
        },
        {
          onSuccess: () => onClose(),
          onError: (e) => setErrorMsg((e as Error)?.message ?? "Не вдалося оновити контакт."),
        },
      );
      return;
    }

    createMut.mutate(
      {
        contact: {
          firstName: parsedName.firstName,
          lastName: parsedName.lastName,
          cCMiddleName: parsedName.middle || undefined,
          cCPosition: position.trim() || undefined,
        },
        assignment: {
          scope,
          targetAccountId: scope === "legal_entity" ? targetAccountId || null : null,
          targetOutletId: scope === "outlet" ? targetOutletId || null : null,
          roles: effectiveRoles,
          isPrimaryForScope: primaryScope,
          // §8 lean: process-primaries are B2B-only — never sent from the lean form.
          primaryForOrders: showB2b && primaryOrders,
          primaryForPayments: showB2b && primaryPayments,
        },
        methods: methodPayload,
        acknowledgedDuplicateContactIds: acknowledged ? dupCandidates.map((c) => c.id) : [],
      },
      {
        onSuccess: (res) => {
          if (res.status === "duplicate_warning") {
            setDupCandidates(res.candidates ?? []);
            setAcknowledged(false); // force an explicit acknowledge before re-save
            setErrorMsg("Знайдено можливий дубль. Перевірте або збережіть попри це.");
            return;
          }
          onClose();
        },
        onError: (e) => setErrorMsg((e as Error)?.message ?? "Не вдалося створити контакт."),
      },
    );
  };

  const labelCls = "text-xs font-semibold uppercase tracking-wide text-muted-foreground";
  const fieldCls =
    "w-full rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  const chip = (on: boolean) =>
    cn(
      "rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors",
      on ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground",
    );

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} aria-hidden>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative flex h-full w-full flex-col overflow-y-auto bg-background shadow-2xl outline-none sm:w-[460px]"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background px-5 py-4">
          <div className="min-w-0">
            <h3 id={headingId} className="flex items-center gap-2 text-base font-semibold">
              <UserPlus className="h-4 w-4 text-muted-foreground" /> {isEdit ? "Редагувати контакт" : "Новий контакт"}
            </h3>
            <p className="mt-0.5 truncate text-xs text-muted-foreground">{clientName}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Закрити"
            className="rounded-md p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-5 px-5 py-5">
          {/* ПІБ (required) */}
          <div className="space-y-1.5">
            <label htmlFor={`${headingId}-pib`} className={labelCls}>
              ПІБ <span className="text-destructive">*</span>
            </label>
            <input
              id={`${headingId}-pib`}
              type="text"
              value={pib}
              autoFocus
              onChange={(e) => setPib(e.target.value)}
              placeholder="Прізвище Імʼя По батькові"
              className={fieldCls}
            />
          </div>

          {/* position (not a role) */}
          <div className="space-y-1.5">
            <label htmlFor={`${headingId}-pos`} className={labelCls}>
              Посада <span className="font-normal normal-case">(не роль)</span>
            </label>
            <input
              id={`${headingId}-pos`}
              type="text"
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              placeholder="напр. Адміністратор"
              className={fieldCls}
            />
          </div>

          {/* roles (multi) — §8 B2B-only (lean: auto «інше» on save) */}
          {showB2b && (
            <div className="space-y-1.5">
              <span className={labelCls}>Ролі <span className="font-normal normal-case">(декілька)</span></span>
              <div className="flex flex-wrap gap-1.5">
                {ROLE_OPTIONS.map((r) => (
                  <button key={r.code} type="button" onClick={() => toggleRole(r.code)} className={chip(roles.includes(r.code))}>
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* methods */}
          <div className="space-y-1.5">
            <span className={labelCls}>{isEdit ? "Додати метод звʼязку" : "Методи звʼязку"}</span>
            {methods.map((m, i) => (
              <div key={i} className="flex items-center gap-2">
                <select
                  value={m.backendType}
                  onChange={(e) =>
                    setMethods((arr) => arr.map((x, j) => (j === i ? { ...x, backendType: e.target.value } : x)))
                  }
                  className={cn(fieldCls, "max-w-[130px]")}
                >
                  {METHOD_TYPES.map((t) => <option key={t.backend} value={t.backend}>{t.label}</option>)}
                </select>
                <input
                  type="text"
                  value={m.value}
                  onChange={(e) => setMethods((arr) => arr.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))}
                  placeholder="+380…"
                  className={fieldCls}
                />
                <span
                  title="Видимість методу визначається правилами доступу (backend/ACL-owned)"
                  className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-border px-2 py-1.5 text-[11px] text-muted-foreground"
                >
                  <Lock className="h-3 w-3" /> за правилами доступу
                </span>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setMethods((arr) => [...arr, { backendType: "phone_mobile", value: "" }])}
              className="text-xs font-semibold text-primary hover:underline"
            >
              + Додати метод
            </button>
            <p className="text-[11px] text-muted-foreground">
              Видимість методу визначається правилами доступу (backend/ACL-owned), НЕ вручну.
            </p>

            {dupCandidates.length > 0 && (
              <div className="mt-2 rounded-md border border-warning-foreground/20 bg-warning px-3 py-2 text-[12px] text-warning-foreground">
                ⚠ <b>Можливий дубль:</b> збігається з{" "}
                {dupCandidates.map((c, i) => (
                  <span key={c.id}>
                    «{c.name}»{i < dupCandidates.length - 1 ? ", " : ""}
                  </span>
                ))}
                .{" "}
                <a href={`/contacts/${dupCandidates[0].id}`} className="font-semibold underline">
                  Переглянути
                </a>{" "}
                <span className="text-[11px]">(merge — лише supervisor)</span>
                {!isEdit && (
                  <label className="mt-1.5 flex items-center gap-1.5 text-[12px]">
                    <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />
                    Це інша людина — зберегти попри дубль
                  </label>
                )}
              </div>
            )}
          </div>

          {/* §8 unclassified fallback — expander reveals the hidden B2B block */}
          {mode === "unclassified" && (
            <button
              type="button"
              onClick={() => setB2bExpanded((v) => !v)}
              className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
            >
              {b2bExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              {b2bExpanded ? "Згорнути додаткові поля" : "Розгорнути додаткові поля"}
            </button>
          )}

          {/* assignment scope (create only) — §8 B2B-only (lean: scope = клієнт) */}
          {!isEdit && showB2b && (
            <div className="space-y-1.5">
              <span className={labelCls}>Привʼязка (scope)</span>
              <div className="flex flex-wrap gap-1.5">
                <button type="button" onClick={() => setScope("holding")} className={chip(scope === "holding")}>холдинг</button>
                {scopes.legalEntities.length > 0 && (
                  <button type="button" onClick={() => setScope("legal_entity")} className={chip(scope === "legal_entity")}>юрособа</button>
                )}
                {scopes.outlets.length > 0 && (
                  <button type="button" onClick={() => setScope("outlet")} className={chip(scope === "outlet")}>точка</button>
                )}
              </div>
              {scope === "legal_entity" && (
                <select value={targetAccountId} onChange={(e) => setTargetAccountId(e.target.value)} className={cn(fieldCls, "mt-1.5")}>
                  <option value="">— оберіть юрособу —</option>
                  {scopes.legalEntities.map((le) => <option key={le.id} value={le.id}>{le.name}</option>)}
                </select>
              )}
              {scope === "outlet" && (
                <select value={targetOutletId} onChange={(e) => setTargetOutletId(e.target.value)} className={cn(fieldCls, "mt-1.5")}>
                  <option value="">— оберіть точку —</option>
                  {scopes.outlets.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              )}
            </div>
          )}

          {/* primary toggles (create only; orders/payments = B2B-only, §8) */}
          {!isEdit && (
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={primaryScope} onChange={(e) => setPrimaryScope(e.target.checked)} />
                Основний для {scopeWord}
              </label>
              {showB2b && (
                <>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={primaryOrders} onChange={(e) => setPrimaryOrders(e.target.checked)} />
                    Основний для замовлень
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={primaryPayments} onChange={(e) => setPrimaryPayments(e.target.checked)} />
                    Основний для оплат
                  </label>
                </>
              )}
            </div>
          )}

          {/* source — system-set, read-only */}
          {!isEdit && (
            <div className="space-y-1.5">
              <span className={labelCls}>Джерело</span>
              <div>
                <span className="inline-flex items-center gap-1 rounded-full bg-info px-2 py-0.5 text-[11px] font-medium text-info-foreground">
                  введено менеджером · system-set
                </span>
              </div>
            </div>
          )}

          {willBeIncomplete && (
            <div className="rounded-md border border-warning-foreground/20 bg-warning px-3 py-2 text-[12px] text-warning-foreground">
              ⚠ Без {showB2b ? "методу звʼязку / ролі" : "методу звʼязку"} контакт збережеться як <b>incomplete</b> (можна зберегти; зʼявиться нагадування доповнити).
            </div>
          )}

          {errorMsg && (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {errorMsg}
            </p>
          )}
        </div>

        <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t bg-background px-5 py-4">
          <button type="button" onClick={onClose} className="rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground">
            Скасувати
          </button>
          <button
            type="button"
            disabled={!canSubmit || (!isEdit && dupCandidates.length > 0 && !acknowledged)}
            onClick={submit}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90",
              (!canSubmit || (!isEdit && dupCandidates.length > 0 && !acknowledged)) && "cursor-not-allowed opacity-50",
            )}
          >
            {(createMut.isPending || updateMut.isPending) && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEdit ? "Зберегти" : "Створити контакт"}
          </button>
        </div>
      </div>
    </div>
  );
}
